import { AloftDate, AloftDateJSON } from '@aloft/shared-objects';
import { APPRAISIFY_ADDRESS, APPRAISIFY_NAME, formatAppraisalType } from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Divider } from '@aloft/toolkit-sui/chakra';

import { useEffect } from 'react';
import { Form, FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form';

import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitEditOrderDetailsForm } from '../../../redux/features/actions/submitEditOrderDetailsForm.ts';
import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { OrderDetailsInput } from '../components/OrderDetailsInput.tsx';
import { useOrderDetailsForm } from '../hooks/useOrderDetailsForm.ts';
import { OrderDetailsFormOutput } from '../schemas.ts';

// Component that calls validation hook from within form context
export const OrderDetails = () => {
  const validation = useAppraisifyValidation();
  const { effectiveDate, intendedUsers, intendedUse } = validation.errors.OrderDetails;

  const dispatch = useAppDispatch();
  const form = useOrderDetailsForm();

  const order = useAppSelector((state) => selectOrder(state));

  useEffect(() => {
    form.reset({
      effectiveDate: order.effectiveDate ? AloftDate.schema.parse(order.effectiveDate) : undefined,
      intendedUsers: order.intendedUsers || '',
      intendedUse: order.intendedUse || '',
    });
  }, [order]);

  const onSubmit: SubmitHandler<OrderDetailsFormOutput> = (data) => {
    dispatch(
      submitEditOrderDetailsForm({
        effectiveDate: data.effectiveDate,
        intendedUsers: data.intendedUsers,
        intendedUse: data.intendedUse,
        id: 'subject',
      }),
    );
  };

  const onError: SubmitErrorHandler<{
    effectiveDate?: AloftDateJSON;
    intendedUsers?: string;
    intendedUse?: string;
  }> = (errors) => {
    console.error(errors);
  };

  const handleChange = () => {
    form
      .handleSubmit(onSubmit, onError)()
      .catch(() => { });
  };

  return (
    <FormProvider {...form}>
      <Form onChange={handleChange}>
        <Flex flexDirection="column" padding="15px">
          <Typography variant="h2">Order Details</Typography>
          <Divider
            borderColor="strokeDefault"
            marginY="10px"
            borderTopWidth="1px"
            borderBottomWidth="0px"
          />
          <Flex gap="15px" lineHeight="32px" flexDirection="column">
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Client Name
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.appraisifyName ?? APPRAISIFY_NAME}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Client Address
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.appraisifyAddress ?? APPRAISIFY_ADDRESS}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Borrower
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.borrowerName || 'N/A'}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Product Type
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.appraisalType ? formatAppraisalType(order.appraisalType) : 'N/A'}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Inspection Date
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.inspectionDate
                  ? AloftDate.schema.parse(order.inspectionDate).toString()
                  : 'N/A'}
              </Typography>
            </Flex>
            <OrderDetailsInput
              control={form.control}
              name="effectiveDate"
              label="Effective Date"
              isRequired
              type="aloftDate"
              errorMessage={effectiveDate?.message}
            />
            <OrderDetailsInput
              control={form.control}
              name="intendedUsers"
              label="Intended User(s)"
              isRequired
              placeholderText="Enter intended user(s)"
              errorMessage={intendedUsers?.message}
            />
            <OrderDetailsInput
              control={form.control}
              name="intendedUse"
              label="Intended Use"
              isRequired
              placeholderText="Enter intended use"
              errorMessage={intendedUse?.message}
            />
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
};
